/**
 * GTM Sales Leader Insights — Phase 1
 *
 * Renders ONLY for role === 'gtm_sales_leader'.
 * Backend: GET /api/role-insights/gtm-sales-leader?range=&start=&end=&lead_owners=
 *
 * Layout (top to bottom, per spec):
 *   • Filter bar    (Date range dropdown + Lead-Owner multi-select)
 *   • 7 KPI cards   (No of Accounts, Master Form, Pending/Completed OTA,
 *                    Document Status, Commercials Approved/Pending)
 *   • Top Performers ranked table
 *   • Weekly Trend  (stacked bar: accounts/day per GTM Sales user)
 *
 * Everything refreshes dynamically when filters change.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, FileText, AlertTriangle, CheckCircle2, ShieldCheck,
  ThumbsUp, Clock, Loader2, RefreshCw, ChevronDown, X, Trophy, TrendingUp,
  Download, ChevronLeft, ChevronRight,
  Coins, IndianRupee, Wallet, FileWarning,
} from 'lucide-react';
import OffboardingInsightsCards from './OffboardingInsightsCards';

// Indian-locale currency formatter — matches LeadModal payment inputs.
const fmtINR = (n) => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `₹${v.toLocaleString('en-IN')}`;
  }
};
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import DrilldownModal, { ClickableCard } from './DrilldownModal';

const RANGES = [
  { key: 'all',           label: 'All' },
  { key: 'this_week',     label: 'This Week' },
  { key: 'this_month',    label: 'This Month' },
  { key: 'last_6_months', label: 'Last 6 Months' },
  { key: 'this_year',     label: 'This Year' },
  { key: 'custom',        label: 'Custom' },
];

const CARD_PALETTE = ['#0d9488', '#6366f1', '#f59e0b', '#10b981', '#8b5cf6', '#0ea5e9', '#ef4444'];

const Card = ({ label, value, Icon, accent, testId }) => (
  <div
    className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow"
    data-testid={testId}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent.bg}`}>
      <Icon size={22} className={accent.fg} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-500 truncate">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-0.5" data-testid={`${testId}-value`}>{value}</p>
    </div>
  </div>
);

/** Lead-Owner multi-select with a chip popover. Kept local — Phase 1 has
 *  no other place that needs it. */
const LeadOwnerMultiSelect = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = (name) => {
    const next = new Set(value);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next));
  };
  return (
    <div className="relative" data-testid="leader-leadowner-filter">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 min-w-[160px] justify-between"
        data-testid="leader-leadowner-toggle"
      >
        <span className="truncate">
          {value.length === 0 ? 'All lead owners' : `${value.length} selected`}
        </span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          <div className="flex items-center justify-between px-2 py-1 mb-1">
            <span className="text-xs font-semibold text-gray-500">GTM Sales users</span>
            {value.length > 0 && (
              <button onClick={() => onChange([])} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            )}
          </div>
          {options.length === 0 && <p className="text-xs text-gray-400 px-2 py-2">No users available</p>}
          {options.map(name => (
            <label key={name} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={value.includes(name)}
                onChange={() => toggle(name)}
                className="accent-teal-600"
                data-testid={`leader-leadowner-option-${name}`}
              />
              <span className="truncate">{name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const GTMSalesLeaderInsights = ({ hideOffboardingCards = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [docSummary, setDocSummary] = useState({ late: 0, urgent: 0, critical: 0, total: 0 });

  // Filter state
  const [range, setRange] = useState('this_week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [trendOffsetDays, setTrendOffsetDays] = useState(0);
  const [drilldown, setDrilldown] = useState(null);

  const openDrilldown = (card, title) => setDrilldown({ card, title });

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ range });
      if (range === 'custom') {
        if (customStart) qs.set('start', customStart);
        if (customEnd) qs.set('end', customEnd);
      }
      if (selectedOwners.length > 0) qs.set('lead_owners', selectedOwners.join(','));
      if (trendOffsetDays !== 0) qs.set('trend_offset_days', String(trendOffsetDays));
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/role-insights/gtm-sales-leader?${qs.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      // Phase 7 — refresh docs-pending escalation summary alongside.
      // Items 5 & 7 — pass the SAME range + lead_owners so the hover
      // breakdown matches the filtered card count.
      try {
        const sumQs = new URLSearchParams({ scope: 'team', range });
        if (range === 'custom') {
          if (customStart) sumQs.set('start', customStart);
          if (customEnd) sumQs.set('end', customEnd);
        }
        if (selectedOwners.length > 0) sumQs.set('lead_owners', selectedOwners.join(','));
        const sumRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/role-insights/docs-pending-summary?${sumQs.toString()}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (sumRes.ok) setDocSummary(await sumRes.json());
      } catch (_) { /* tooltip is best-effort */ }
    } catch (e) {
      setError(e.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [range, customStart, customEnd, selectedOwners, trendOffsetDays]);

  // When range / lead-owners change, snap the trend window back to "now".
  useEffect(() => { setTrendOffsetDays(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range, customStart, customEnd, JSON.stringify(selectedOwners)]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  // CSV export — uses the SAME filtered payload the user is looking at,
  // so the exported file is always consistent with the current view.
  const exportCSV = useCallback(() => {
    if (!data) return;
    const lines = [];
    const filters = data.filters_applied || {};
    lines.push('# GTM Sales Leader Insights export');
    lines.push(`# range,${filters.range || 'all'}`);
    if (filters.start) lines.push(`# start,${filters.start}`);
    if (filters.end) lines.push(`# end,${filters.end}`);
    if (filters.lead_owners?.length) lines.push(`# lead_owners,"${filters.lead_owners.join('; ')}"`);
    lines.push('');

    lines.push('## Top Performers');
    lines.push('Rank,Name,Accounts');
    (data.top_performers || []).forEach((p, i) => {
      lines.push(`${i + 1},"${(p.name || '').replace(/"/g, '""')}",${p.accounts}`);
    });
    lines.push('');

    lines.push('## Weekly Trend (accounts created per day per owner)');
    const owners = data.weekly_trend?.owners || [];
    lines.push(['Date', ...owners].map(c => `"${String(c).replace(/"/g, '""')}"`).join(','));
    (data.weekly_trend?.rows || []).forEach(r => {
      const row = [r.date, ...owners.map(o => r[o] ?? 0)];
      lines.push(row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gtm_leader_insights_${filters.range || 'all'}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data]);

  const cards = data?.cards || {};
  const topPerformers = data?.top_performers || [];
  const trend = data?.weekly_trend || { days: [], owners: [], rows: [] };
  const ownersAvailable = data?.lead_owners_available || [];

  // Trend chart rows already shaped {date, owner1: n, owner2: n, ...}
  const chartOwners = useMemo(() => trend.owners.slice(0, 7), [trend.owners]);

  return (
    <div className="space-y-5" data-testid="gtm-sales-leader-insights">
      {/* Header + Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Team Insights</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              All accounts created by the GTM Sales team.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500">Range</label>
              <select
                value={range}
                onChange={e => setRange(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="leader-range-select"
              >
                {RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            {range === 'custom' && (
              <div className="flex items-center gap-2" data-testid="leader-custom-range">
                <input
                  type="date" value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                  data-testid="leader-custom-start"
                />
                <span className="text-xs text-gray-400">to</span>
                <input
                  type="date" value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                  data-testid="leader-custom-end"
                />
              </div>
            )}
            <LeadOwnerMultiSelect
              options={ownersAvailable}
              value={selectedOwners}
              onChange={setSelectedOwners}
            />
            <button
              onClick={fetchInsights}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              data-testid="leader-refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={exportCSV}
              disabled={loading || !data}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              data-testid="leader-export-csv"
              title="Export Top Performers + Weekly Trend (with applied filters)"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="leader-error">
          {error}
        </div>
      )}

      {/* KPI Cards (7) — N×3 matrix */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="leader-cards-grid">
          <ClickableCard label="No of Accounts" value={cards.no_of_accounts ?? 0}
            Icon={Briefcase} accent={{ bg: 'bg-teal-50', fg: 'text-teal-600' }}
            testId="leader-card-accounts"
            onClick={() => openDrilldown('no_of_accounts', 'No of Accounts')} />
          <ClickableCard label="Master Form" value={cards.master_form_completed ?? 0}
            Icon={FileText} accent={{ bg: 'bg-indigo-50', fg: 'text-indigo-600' }}
            testId="leader-card-master-form"
            onClick={() => openDrilldown('master_form', 'Master Form Completed')} />
          <ClickableCard label="Pending OTA Listing" value={cards.pending_ota_listing ?? 0}
            Icon={AlertTriangle} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }}
            testId="leader-card-pending-ota"
            onClick={() => openDrilldown('pending_ota', 'Pending OTA Listings')} />
          <ClickableCard label="Completed OTA Listing" value={cards.completed_ota_listing ?? 0}
            Icon={CheckCircle2} accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }}
            testId="leader-card-completed-ota"
            onClick={() => openDrilldown('completed_ota', 'Completed OTA Listings')} />
          <ClickableCard label="Document Status" value={cards.document_status_completed ?? 0}
            Icon={ShieldCheck} accent={{ bg: 'bg-violet-50', fg: 'text-violet-600' }}
            testId="leader-card-doc-status"
            onClick={() => openDrilldown('document_status', 'Document Status')} />
          <ClickableCard label="Commercials Approved" value={cards.commercials_approved ?? 0}
            Icon={ThumbsUp} accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }}
            testId="leader-card-commercials-approved"
            onClick={() => openDrilldown('commercials_approved', 'Commercials Approved')} />
          <ClickableCard label="Commercials Pending" value={cards.commercials_pending ?? 0}
            Icon={Clock} accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }}
            testId="leader-card-commercials-pending"
            onClick={() => openDrilldown('commercials_pending', 'Commercials Pending')} />
          {/* ── Phase 6 — Financial + Documents Pending ────────────────── */}
          <ClickableCard label="Total Token" value={fmtINR(cards.total_token)}
            Icon={Coins} accent={{ bg: 'bg-yellow-50', fg: 'text-yellow-600' }}
            testId="leader-card-total-token"
            onClick={() => openDrilldown('total_token', 'Total Token')} />
          <ClickableCard label="Total Setup Agreed" value={fmtINR(cards.total_setup_agreed)}
            Icon={IndianRupee} accent={{ bg: 'bg-cyan-50', fg: 'text-cyan-600' }}
            testId="leader-card-total-setup-agreed"
            onClick={() => openDrilldown('total_setup_agreed', 'Total Setup Agreed')} />
          <ClickableCard label="Total Pending Payment" value={fmtINR(cards.total_pending_payment)}
            Icon={Wallet} accent={{ bg: 'bg-pink-50', fg: 'text-pink-600' }}
            testId="leader-card-total-pending-payment"
            onClick={() => openDrilldown('total_pending_payment', 'Total Pending Payment')} />
          <div className="relative group" data-testid="leader-card-documents-pending-wrap">
            <ClickableCard label="Documents Pending" value={cards.documents_pending ?? 0}
              Icon={FileWarning} accent={{ bg: 'bg-orange-50', fg: 'text-orange-600' }}
              testId="leader-card-documents-pending"
              onClick={() => openDrilldown('documents_pending', 'Documents Pending')} />
            <div
              className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-56 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white rounded-lg shadow-xl px-3 py-2 text-xs"
              data-testid="leader-doc-pending-tooltip"
            >
              <div className="flex items-center justify-between py-0.5">
                <span className="text-gray-300">Late</span>
                <span className="font-bold text-amber-300" data-testid="leader-doc-late">{docSummary.late}</span>
              </div>
              <div className="flex items-center justify-between py-0.5">
                <span className="text-gray-300">Urgent</span>
                <span className="font-bold text-orange-300" data-testid="leader-doc-urgent">{docSummary.urgent}</span>
              </div>
              <div className="flex items-center justify-between py-0.5">
                <span className="text-gray-300">Critical</span>
                <span className="font-bold text-rose-300" data-testid="leader-doc-critical">{docSummary.critical}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Performers + Weekly Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="leader-top-performers">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" />
            <h3 className="text-sm font-bold text-gray-800">Top Performers</h3>
            <span className="text-xs text-gray-400 ml-auto">by accounts created</span>
          </div>
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {topPerformers.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-8">No accounts in selected range</p>
            )}
            {topPerformers.map((p, idx) => (
              <div
                key={p.name}
                className="flex items-center gap-3 px-5 py-3"
                data-testid={`leader-top-performer-${idx + 1}`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-700'
                    : idx === 1 ? 'bg-gray-100 text-gray-700'
                    : idx === 2 ? 'bg-orange-100 text-orange-700'
                    : 'bg-teal-50 text-teal-700'
                  }`}
                >
                  {idx + 1}
                </div>
                <span className="flex-1 text-sm font-medium text-gray-800 truncate">{p.name}</span>
                <span className="text-sm font-bold text-gray-900" data-testid={`leader-top-performer-${idx + 1}-count`}>{p.accounts}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden lg:col-span-2" data-testid="leader-weekly-trend">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <TrendingUp size={18} className="text-teal-600" />
            <h3 className="text-sm font-bold text-gray-800">Weekly Trend</h3>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setTrendOffsetDays(d => d - 7)}
                className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
                title="Previous 7 days"
                data-testid="leader-trend-prev"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-[11px] text-gray-500 tabular-nums" data-testid="leader-trend-window">
                {trend.days[0] || '—'} → {trend.days[trend.days.length - 1] || '—'}
              </span>
              <button
                type="button"
                onClick={() => setTrendOffsetDays(d => d + 7)}
                disabled={trendOffsetDays >= 0}
                className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Next 7 days"
                data-testid="leader-trend-next"
              >
                <ChevronRight size={16} />
              </button>
              {trendOffsetDays !== 0 && (
                <button
                  type="button"
                  onClick={() => setTrendOffsetDays(0)}
                  className="text-[11px] text-teal-600 hover:underline"
                  data-testid="leader-trend-reset"
                >
                  reset
                </button>
              )}
            </div>
          </div>
          <div className="p-5" style={{ height: 320, minHeight: 320 }}>
            {trend.rows.length === 0 ? (
              <p className="text-xs text-gray-400 text-center pt-10">No data in selected range</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {chartOwners.map((owner, idx) => (
                    <Bar
                      key={owner}
                      dataKey={owner}
                      stackId="a"
                      fill={CARD_PALETTE[idx % CARD_PALETTE.length]}
                      radius={idx === chartOwners.length - 1 ? [4, 4, 0, 0] : 0}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {drilldown && (
        <DrilldownModal
          title={drilldown.title}
          card={drilldown.card}
          scope="team"
          leadOwners={selectedOwners.length > 0 ? selectedOwners : null}
          rangeParams={{ range, ...(range === 'custom' ? { start: customStart, end: customEnd } : {}) }}
          onClose={() => setDrilldown(null)}
          rowAction={drilldown.card === 'documents_pending' ? {
            label: 'Open',
            onClick: (row) => { setDrilldown(null); navigate(`/leads/${row.lead_id}`); },
          } : null}
        />
      )}

      {/* Phase 16 — Offboarding cards (skip when embedded in Executive view). */}
      {!hideOffboardingCards && <OffboardingInsightsCards />}
    </div>
  );
};

export default GTMSalesLeaderInsights;
